import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');
  };

  const navLinks = [
    { label: t('nav_home'), to: '/' },
    { label: t('nav_products'), to: '/products' },
    { label: t('nav_contact'), to: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
            <div
              className="absolute"
              style={{
                width: 0,
                height: 0,
                borderLeft: '11px solid transparent',
                borderRight: '3px solid transparent',
                borderBottom: '36px solid #1a1a1a',
                top: 2,
                left: 3,
                transform: 'rotate(-8deg)',
              }}
            />
            <div
              className="absolute"
              style={{
                width: 0,
                height: 0,
                borderLeft: '3px solid transparent',
                borderRight: '11px solid transparent',
                borderBottom: '36px solid #dc2626',
                top: 2,
                right: 3,
                transform: 'rotate(8deg)',
              }}
            />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="font-black text-xl tracking-tight text-gray-900"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              MAX<span className="text-red-600">SAILS</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-gray-400 hidden sm:block">
              Canada
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                location.pathname === link.to
                  ? 'text-red-600'
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 text-xs font-semibold text-gray-600 hover:border-red-400 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap"
            aria-label="Toggle language"
          >
            <i className="ri-global-line text-sm" />
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </button>

          <Link
            to="/contact"
            className="ml-2 px-5 py-2 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav_quote')}
          </Link>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-2xl text-gray-800 ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className={`text-sm font-medium py-1 cursor-pointer ${
                location.pathname === link.to ? 'text-red-600' : 'text-gray-700'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 text-sm font-medium py-1 text-gray-700 cursor-pointer"
          >
            <i className="ri-global-line" />
            {i18n.language === 'en' ? 'Français' : 'English'}
          </button>
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className="mt-1 px-5 py-2 rounded-full bg-red-600 text-white text-sm font-semibold text-center cursor-pointer"
          >
            {t('nav_quote')}
          </Link>
        </div>
      )}
    </header>
  );
}
